import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, TypographyMuted, TypographyP, TypographyPMedium } from "@finster/ui";
import { Search, X } from "lucide-react";
import type { Tab } from "../types";
import { chatRowLabel, chatScopeLabel } from "./chatTabLabels";

interface AllChatsViewProps {
  sortedTabs: Tab[];
  activeTabId: string;
  onSelectTab: (tabId: string) => void;
  onClose: () => void;
  searchFocusKey?: number;
}

function matchesSearch(tab: Tab, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const slideNum = String(tab.session.slideNumber ?? tab.session.slideIndex + 1);
  if (slideNum.includes(q) || `slide ${slideNum}`.includes(q) || chatScopeLabel(tab).toLowerCase().includes(q)) {
    return true;
  }

  return tab.session.messages.some((m) => m.role === "user" && m.content.toLowerCase().includes(q));
}

function formatChatDate(timestamp: number): string {
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit" }).format(
    new Date(timestamp)
  );
}

export const AllChatsView: React.FC<AllChatsViewProps> = ({
  sortedTabs,
  activeTabId,
  onSelectTab,
  onClose,
  searchFocusKey = 0,
}) => {
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const tabsWithMessages = useMemo(
    () => sortedTabs.filter((t) => t.session.messages.length > 0),
    [sortedTabs]
  );

  const filteredTabs = useMemo(
    () =>
      [...tabsWithMessages]
        .sort((a, b) => b.session.updatedAt - a.session.updatedAt)
        .filter((t) => matchesSearch(t, searchText)),
    [tabsWithMessages, searchText]
  );

  useEffect(() => {
    if (searchFocusKey <= 0) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [searchFocusKey]);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {/* Header — matches ChatView layout */}
      <div className="flex-none px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close all chats"
            className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
          <TypographyP>All Chats ({tabsWithMessages.length})</TypographyP>
        </div>
      </div>

      {/* Scrollable body */}
      <main className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pt-1 pb-3">
        {/* Search */}
        <div className="relative mb-3 flex-none">
          <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search chats"
            className="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
          />
        </div>

        {/* Empty states */}
        {tabsWithMessages.length === 0 && (
          <div className="flex flex-1 items-center justify-center">
            <TypographyMuted>No chats yet.</TypographyMuted>
          </div>
        )}

        {tabsWithMessages.length > 0 && filteredTabs.length === 0 && (
          <div className="flex flex-1 items-center justify-center">
            <TypographyMuted>No chats match your search.</TypographyMuted>
          </div>
        )}

        {/* Chat list */}
        {filteredTabs.length > 0 && (
          <div className="min-w-0 overflow-hidden rounded-lg border border-border">
            {filteredTabs.map((tab, i) => {
              const isActive = tab.id === activeTabId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`block w-full cursor-pointer px-3 py-2.5 text-left transition-colors hover:bg-primary/10 ${
                    isActive ? "bg-muted/40" : ""
                  } ${i < filteredTabs.length - 1 ? "border-b border-border" : ""}`}
                  onClick={() => onSelectTab(tab.id)}
                >
                  <TypographyPMedium
                    className="truncate text-[13px] leading-snug text-foreground"
                    title={chatRowLabel(tab)}
                  >
                    {chatRowLabel(tab)}
                  </TypographyPMedium>
                  <div className="mt-1 flex items-center justify-between">
                    <TypographyMuted className="text-[11px]">
                      {formatChatDate(tab.session.updatedAt)}
                    </TypographyMuted>
                    <Badge variant="default" shape="round" className="text-[10px]">
                      {chatScopeLabel(tab)}
                    </Badge>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};
